import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@heroui/react';
import { DatePickerField } from '@/components/datePickerField';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    formatBpsPercent,
    RATE_FIELDS,
    regimeLabelKey,
} from '../../settingsMeta';
import type { UseRatesOnDateResult } from './useRatesOnDate';

type RatesOnDateViewProps = {
    lookup: UseRatesOnDateResult;
};

/**
 * The date lookup.
 *
 * The date field and the labels stay rendered throughout; only the resolved
 * figures skeleton, so changing the date never collapses the layout.
 */
export const RatesOnDateView: React.FC<RatesOnDateViewProps> = ({ lookup }) => {
    const { t, i18n } = useTranslation('pages');

    return (
        <section
            aria-label={t('settings.onDate.heading')}
            className="flex w-full flex-col gap-4"
            {...getNodeTestIdProp('settings-on-date')}
        >
            <div className="flex flex-col gap-1">
                <h2 className="text-sm font-semibold text-foreground">
                    {t('settings.onDate.heading')}
                </h2>
                <p className="text-xs text-muted">
                    {t('settings.onDate.intro')}
                </p>
            </div>

            <div className="max-w-xs">
                <DatePickerField
                    id="settings-on-date-picker"
                    label={t('settings.onDate.label')}
                    name="lookupDate"
                    onChange={lookup.onDateChange}
                    value={lookup.date}
                />
            </div>

            {lookup.isBeforeLedger ? (
                <p
                    className="text-sm text-muted"
                    {...getNodeTestIdProp('settings-on-date-before-ledger')}
                >
                    {t('settings.onDate.beforeLedger')}
                </p>
            ) : (
                <div
                    className="flex flex-col gap-3 rounded-xl bg-surface-secondary p-4"
                    {...getNodeTestIdProp('settings-on-date-result')}
                >
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-foreground">
                            {lookup.isLoading || !lookup.set ? (
                                <Skeleton className="h-5 w-32 rounded" />
                            ) : (
                                t('settings.onDate.inForceFrom', {
                                    date: lookup.set.effectiveFrom,
                                })
                            )}
                        </span>
                        {!lookup.isLoading && lookup.set ? (
                            <span className="text-xs text-muted">
                                {t(regimeLabelKey(lookup.set.regime))}
                            </span>
                        ) : null}
                    </div>

                    <dl className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
                        {RATE_FIELDS.map((field) => (
                            <div className="flex flex-col" key={field.key}>
                                <dt className="text-xs text-muted">
                                    {t(field.labelKey)}
                                </dt>
                                <dd className="text-sm tabular-nums text-foreground">
                                    {lookup.isLoading || !lookup.set ? (
                                        <Skeleton className="h-5 w-12 rounded" />
                                    ) : (
                                        formatBpsPercent(
                                            lookup.set.rates[field.key],
                                            i18n.language,
                                        )
                                    )}
                                </dd>
                            </div>
                        ))}
                    </dl>

                    {!lookup.isLoading && lookup.set?.note ? (
                        <p className="text-sm text-muted">{lookup.set.note}</p>
                    ) : null}
                </div>
            )}
        </section>
    );
};
